import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  FileSignature,
  RefreshCw,
  Send,
  AlertTriangle,
  ShieldAlert,
  Search,
} from 'lucide-react'
import {
  useGetRollout,
  useGetRolloutSummary,
  useSendNewContract,
  useSendNewContractBulk,
} from '../hooks/backend/contracts'
import { useAccess } from '../hooks/useAccess'
import { useCapabilities } from '../hooks/useCapabilities'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Spinner } from '../lib/shadcn/spinner'
import { toast } from '../lib/shadcn/sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../lib/shadcn/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../lib/shadcn/alert-dialog'
import { RolloutTable } from '../components/contracts/RolloutTable'
import {
  type RolloutRow,
  type RolloutSummary,
  type StatusFilter,
  STATUS_FILTERS,
  matchesFilter,
  statusCategory,
} from '../utils/contractRollout'

type SendResult = { ok?: boolean; refused?: boolean; reason?: string } | null
type BulkResult = { queued?: number; refused?: number } | null

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-2xl font-semibold tabular-nums text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  )
}

export default function ContractRollout() {
  const { canAct } = useAccess()
  const { loading: capsLoading, canView, canEditDrivers } = useCapabilities()
  const canSend = canEditDrivers && canAct

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<StatusFilter>('all')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [sentIds, setSentIds] = useState<Set<string>>(new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const rollout = useGetRollout()
  const summary = useGetRolloutSummary()
  const { trigger: triggerSend } = useSendNewContract()
  const { trigger: triggerBulk } = useSendNewContractBulk()

  const rolloutTrigger = rollout.trigger
  const summaryTrigger = summary.trigger

  const loadRollout = useCallback(() => {
    rolloutTrigger({}, { skipCache: true })
  }, [rolloutTrigger])

  const loadSummary = useCallback(() => {
    summaryTrigger({}, { skipCache: true })
  }, [summaryTrigger])

  useEffect(() => {
    if (canView) {
      loadRollout()
      loadSummary()
    }
  }, [canView, loadRollout, loadSummary])

  // Sends are asynchronous: after a successful trigger we re-fetch both the
  // rollout and the summary a few seconds later so the new status appears.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    refreshTimer.current = setTimeout(() => {
      loadRollout()
      loadSummary()
    }, 4000)
  }, [loadRollout, loadSummary])

  useEffect(() => {
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current)
    }
  }, [])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await Promise.all([
        rolloutTrigger({}, { skipCache: true }).result,
        summaryTrigger({}, { skipCache: true }).result,
      ])
    } finally {
      setRefreshing(false)
    }
  }

  const send = useCallback(
    async (row: RolloutRow, mode: 'send' | 'resend') => {
      setBusyId(row.driver_id)
      try {
        const result = (await triggerSend({
          driver_id: row.driver_id,
          mode,
        }).result) as SendResult
        if (result && result.ok === false) {
          toast.error('Contract not sent', {
            description: result.reason || 'The request was refused.',
          })
        } else {
          toast.success(mode === 'resend' ? 'Contract resent' : 'Contract sent')
          if (mode === 'send') {
            setSentIds((prev) => new Set(prev).add(row.driver_id))
          }
          scheduleRefresh()
        }
      } catch (err) {
        toast.error('Could not send contract', {
          description: err instanceof Error ? err.message : String(err),
        })
      } finally {
        setBusyId(null)
      }
    },
    [triggerSend, scheduleRefresh],
  )

  const handleSend = useCallback((row: RolloutRow) => void send(row, 'send'), [send])
  const handleResend = useCallback((row: RolloutRow) => void send(row, 'resend'), [send])

  const handleBulk = async () => {
    setBulkBusy(true)
    try {
      const result = (await triggerBulk({}).result) as BulkResult
      const queued = result?.queued ?? 0
      const refused = result?.refused ?? 0
      toast.success('Bulk send queued', {
        description: `${queued} queued, ${refused} refused.`,
      })
      setSentIds((prev) => {
        const next = new Set(prev)
        for (const row of allRows) {
          if (statusCategory(row) === 'not_sent') next.add(row.driver_id)
        }
        return next
      })
      scheduleRefresh()
    } catch (err) {
      toast.error('Could not queue bulk send', {
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setBulkBusy(false)
      setBulkOpen(false)
    }
  }

  const allRows = (rollout.data as RolloutRow[] | undefined) ?? []
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return allRows.filter((r) => {
      if (!matchesFilter(r, filter)) return false
      if (!term) return true
      const hay = [r.driver_name, r.primary_email].filter(Boolean).join(' ').toLowerCase()
      return hay.includes(term)
    })
  }, [allRows, search, filter])

  const s = (summary.data as RolloutSummary | null | undefined) ?? null
  const wouldSend = s?.would_send ?? 0

  if (capsLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access...
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            The new contract rollout is not available to your account.
          </p>
          <p className="text-xs text-muted-foreground">
            Contact an admin if you need access.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <FileSignature className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">New contract rollout</h1>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canSend ? (
              <Button
                size="sm"
                onClick={() => setBulkOpen(true)}
                disabled={bulkBusy || wouldSend === 0}
                className="gap-1.5"
              >
                {bulkBusy ? <Spinner className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                Send to all unsigned ({wouldSend})
              </Button>
            ) : null}
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Contracts for drivers already employed. New contracts are issued on the agreement in force today (v6 from 3 October 2026); drivers who signed v5 are offered Addendum No. 1 on the Agreements page instead.
        </p>
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Summary strip */}
        <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-5">
          <SummaryStat label="Signed" value={s?.signed ?? 0} />
          <SummaryStat label="Awaiting signature" value={s?.awaiting_signature ?? 0} />
          <SummaryStat label="Not yet sent" value={s?.would_send ?? 0} />
          <SummaryStat label="No email on file" value={s?.blocked_no_email ?? 0} />
          <SummaryStat label="Blocked" value={s?.excluded_dismissed ?? 0} />
        </div>

        {/* Amber notice */}
        <div className="mx-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Vehicle allocation from the week of Monday 31 August 2026 requires a signed new contract.
            Drivers who have not signed will not be seated.
          </span>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 p-4">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              className="pl-9"
            />
          </div>
          <Select value={filter} onValueChange={(v) => setFilter(v as StatusFilter)}>
            <SelectTrigger className="h-10 w-[190px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
            {rollout.loading ? '…' : `${rows.length} driver${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>

        {/* Table / cards */}
        <RolloutTable
          rows={rows}
          loading={rollout.loading}
          error={rollout.error}
          canSend={canSend}
          busyId={busyId}
          sentIds={sentIds}
          onSend={handleSend}
          onResend={handleResend}
        />
      </div>

      {/* Bulk confirmation */}
      <AlertDialog open={bulkOpen} onOpenChange={(open) => !bulkBusy && setBulkOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send the new contract to all unsigned drivers?</AlertDialogTitle>
            <AlertDialogDescription>
              This emails every unsigned driver a new v5 contract to review and sign. Current status:{' '}
              {s?.signed ?? 0} signed, {s?.awaiting_signature ?? 0} awaiting signature,{' '}
              {s?.would_send ?? 0} not yet sent, {s?.blocked_no_email ?? 0} with no email on file,{' '}
              {s?.excluded_dismissed ?? 0} blocked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleBulk()
              }}
              disabled={bulkBusy}
            >
              {bulkBusy && <Spinner className="h-4 w-4" />}
              Send to all unsigned
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
